import React from 'react';

/**
 * InlineSuggestion - Displays ghost text suggestion inline with user input
 *
 * This component shows a semi-transparent suggestion text that appears
 * after the user's cursor. Users can press Tab to accept the suggestion.
 *
 * Props:
 * - suggestion: The suggested text to display
 * - visible: Whether the suggestion should be visible
 * - textBeforeCursor: The text before the current cursor position
 * - style: Optional additional styles
 */

const InlineSuggestion = ({
  suggestion = '',
  visible = false,
  textBeforeCursor = '',
  style = {},
}) => {
  if (!visible || !suggestion) {
    return null;
  }

  return (
    <div
      className="inline-suggestion-container pointer-events-none absolute inset-0 overflow-hidden"
      style={style}
    >
      {/* Invisible text that takes up space of existing content */}
      <span className="invisible whitespace-pre-wrap">{textBeforeCursor}</span>

      {/* Ghost text suggestion */}
      <span className="text-gray-400 dark:text-[#555555] opacity-70 whitespace-pre-wrap">
        {suggestion}
      </span>

      <style jsx>{`
        .inline-suggestion-container {
          font-family: inherit;
          font-size: inherit;
          line-height: inherit;
          letter-spacing: inherit;
          word-spacing: inherit;
          padding: inherit;
        }
      `}</style>
    </div>
  );
};

/**
 * GhostTextOverlay - A more sophisticated ghost text overlay for rich text editors
 *
 * This version positions itself relative to the cursor position and can be
 * used with contenteditable elements or textareas.
 */
export const GhostTextOverlay = ({
  suggestion = '',
  visible = false,
  position = { top: 0, left: 0 },
  onAccept,
}) => {
  if (!visible || !suggestion) {
    return null;
  }

  return (
    <div
      className="ghost-text-overlay pointer-events-none absolute z-50"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <span className="text-gray-400 dark:text-[#555555] opacity-60 whitespace-pre">
        {suggestion}
      </span>

      {/* Tab hint */}
      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 dark:text-[#555555] bg-gray-100 dark:bg-[#1A1A1A] rounded border border-gray-200 dark:border-[#333333] opacity-60">
        Tab
      </span>

      <style jsx>{`
        .ghost-text-overlay {
          font-family: inherit;
          font-size: inherit;
          line-height: inherit;
        }
      `}</style>
    </div>
  );
};

/**
 * TabHint - Small hint showing Tab key can be pressed
 */
export const TabHint = ({ visible = false, className = '' }) => {
  if (!visible) {
    return null;
  }

  return (
    <span
      className={`inline-flex items-center px-1 py-0.5 text-[9px] font-medium text-gray-400 dark:text-[#555555] bg-gray-100 dark:bg-[#1A1A1A] rounded border border-gray-200 dark:border-[#333333] ${className}`}
    >
      Tab ↵
    </span>
  );
};

export default InlineSuggestion;
